import { useCallbackRef } from "../lifecycle";
import { updateCheckoutState } from "src/utils/redux/slices/checkout";
import {
  createOrderRequest,
  getOrderRequest,
  executeOrderRequest,
} from "src/utils/api/purchase/ordersCheckoutApi";
import { useAppDispatch } from "../redux";
import {
  setCheckoutOrders,
  setHasInitializationFailed,
  setCheckoutSaleItem,
  SaleItemInfo,
  setCheckoutCustomerDetails,
  setCheckoutOrderOperation,
} from "src/utils/redux/slices/checkoutOrders";
import { errorConstants } from "src/utils/services/PurchaseErrorMessages";
import { convertCyclesToPlan } from "src/utils/orderConversions";
import {
  legacyPurchaseRoot,
  setRecommendedPlan,
  setTrialFee,
} from "src/utils/redux/slices/recommendedPlan";
import { captureException } from "src/utils/error";
import { trackTask } from "src/utils/monitoring/events/tasks";
import { applyPromoCode } from "src/utils/api/purchase/checkout";
import {
  convertMoneyProtoToNumber,
  convertNumberToMoney,
} from "src/utils/noomContracts/moneyProto";
import {
  clearPromoCode,
  setPromoError,
  updatePromoDiscountAmount,
  updatePromoOrderStatus,
} from "src/utils/redux/slices/promoCode";
import {
  NOOM_PLAN_ID_TELEX283,
  NOOM_PLAN_ID_TELEX293,
  NOOM_PLAN_ID_TELEX294a,
  NOOM_PLAN_ID_TELEX294b,
} from "src/utils/plansMapping";
import { trackBuyflowEvent } from "src/utils/api/tracker";
import i18n from "src/locales";
import { BillingCyclePreviewProto } from "@noom/noom-contracts/noom_contracts/billing/bms/subscriptions/billing_cycle_preview";
import { PurchaseOperationProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/order_operation";
import getStore from "src/utils/redux/store";
import {
  isApplePurchaseLinkoutEnabled,
  isOrdersPreviewPurchaseCheckout,
  isOrdersPurchaseCheckout,
  isProductSwitchCheckout,
} from "src/utils/userSegment/features";
import { getActiveUser } from "@noom/hive-auth";
import { DiscountSummaryProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/discount_summary";
import { OrderProto } from "@noom/noom-contracts/noom_contracts/billing/bms/orders/orders_service";
import { MoneyProto } from "@noom/noom-contracts/noom_contracts/lib/money";
import { getUserCurrency } from "src/utils/plans";

function remapURLNoomPlanId(noomPlanId: string) {
  if (noomPlanId && /\/purchase(-hm)?\//.test(window.location.pathname)) {
    const { history } = window;
    history.replaceState(
      history.state,
      "",
      legacyPurchaseRoot() + window.location.hash
    );
  }
}

export function extractBillingCycles(order: OrderProto): {
  initialBillingCycle?: BillingCyclePreviewProto;
  recurringBillingCycle?: BillingCyclePreviewProto;
} {
  let billingCycles: BillingCyclePreviewProto[] | undefined;
  for (const action of order.executionActions ?? []) {
    if (action.createSubscription?.billingCycles?.length) {
      billingCycles = action.createSubscription.billingCycles;
      break;
    }
    if (action.modifySubscription?.billingCycles?.length) {
      billingCycles = action.modifySubscription.billingCycles;
      break;
    }
    if (action.updatePaymentSchedule?.billingCycles?.length) {
      billingCycles = action.updatePaymentSchedule.billingCycles;
      break;
    }
  }
  if (!billingCycles?.length) {
    throw new Error("Could not find billing cycles in order response");
  }
  if (billingCycles.length > 2) {
    throw new Error(
      `Expected at most 2 billing cycles but received ${billingCycles.length}`
    );
  }
  if (billingCycles.length === 1) {
    return {
      recurringBillingCycle: billingCycles[0],
    };
  }
  return {
    initialBillingCycle: billingCycles[0],
    recurringBillingCycle: billingCycles[1],
  };
}

export function useOrdersCheckoutMethods() {
  const dispatch = useAppDispatch();
  const accessCode = getActiveUser()?.accessCode;

  /**
   * Extracts the refund amount from a switch order's credit note.
   * Returns undefined if no credit note is found (e.g., for purchase orders).
   */
  function getRefundAmount(order: OrderProto): MoneyProto | undefined {
    for (const action of order.executionActions ?? []) {
      const creditNoteAmount =
        action.createCreditNote?.creditNote?.lineItems?.[0]?.amount;
      if (
        creditNoteAmount &&
        (creditNoteAmount.units || creditNoteAmount.nanos)
      ) {
        return creditNoteAmount;
      }
    }
    return undefined;
  }

  async function getDiscountSummary(
    promoCode: string
  ): Promise<DiscountSummaryProto | undefined> {
    // Switch to billing promo validation service once it is ready
    // https://github.com/noom/noom-contracts/blob/master/noom_contracts/billing/discounts/promotions_service.proto#L120
    // TODO: MGEXP-3026
    // Get the sale item directly from state to avoid using stale state from the hook
    const { legacyNoomPlanId } = getStore().getState().checkoutOrders?.saleItem;
    const result = await dispatch(
      applyPromoCode({ noom_plan_id: legacyNoomPlanId }, promoCode)
    );
    const { promoDiscountAmount, promoDiscountPercentage, promoCodeApplied } =
      result?.newPromoCode || {};
    if (!promoCodeApplied) {
      return undefined;
    }
    const flatDiscount = promoDiscountAmount
      ? convertNumberToMoney(promoDiscountAmount, getUserCurrency())
      : undefined;
    if (promoDiscountPercentage) {
      return {
        percentageDiscount: {
          percentage: { value: promoDiscountPercentage.toString() },
        },
      };
    }
    if (flatDiscount) {
      return {
        fixedDiscount: {
          amount: flatDiscount,
          percentageDiscountConvertedToFixed: false,
        },
      };
    }
    return undefined;
  }

  async function setPurchaseOperation(promoCode?: string) {
    const saleItemInfo: SaleItemInfo | undefined =
      getStore().getState().checkoutOrders?.saleItem;
    const legacyNoomPlanId = saleItemInfo?.legacyNoomPlanId;

    const isTelexPlanWithoutPromoSupport =
      legacyNoomPlanId === NOOM_PLAN_ID_TELEX283 ||
      legacyNoomPlanId === NOOM_PLAN_ID_TELEX293 ||
      legacyNoomPlanId === NOOM_PLAN_ID_TELEX294a ||
      legacyNoomPlanId === NOOM_PLAN_ID_TELEX294b;
    // Telex 283/293/294a/294b plans do not support promo codes; clear and proceed without discount to avoid "Invalid discount summary".
    if (isTelexPlanWithoutPromoSupport && promoCode) {
      dispatch(clearPromoCode());
    }

    let discountSummary: DiscountSummaryProto | undefined;
    if (!isTelexPlanWithoutPromoSupport && promoCode) {
      dispatch(updatePromoOrderStatus("in_progress"));
      try {
        discountSummary = await getDiscountSummary(promoCode);
        if (!discountSummary) {
          throw new Error("Invalid discount summary");
        }
      } catch (ex) {
        trackBuyflowEvent("CheckoutOrdersOperationFailed", {
          operationName: "applyPromoDiscount",
          error: ex?.message,
        });
        captureException(ex);
        dispatch(
          setPromoError({
            promoCodeError: i18n.t("checkout:genericPromoError"),
            promoCodeErrorType: "UnknownError",
          })
        );
        dispatch(updatePromoOrderStatus("failed"));
        discountSummary = undefined;
      }
    }
    if (!saleItemInfo) {
      throw new Error(
        "Sale item not initialized before setting purchase operation"
      );
    }
    const purchaseOperation: PurchaseOperationProto = {
      trialSaleItemId: saleItemInfo.trialSaleItemId,
      saleItemId: saleItemInfo.saleItemId,
      metadata: {
        purchaseFlow: "WEB",
      },
      discountSummary,
    };
    dispatch(setCheckoutOrderOperation({ purchaseOperation }));
  }

  async function createOrderAndBillingCyclePreview(): Promise<{
    initialBillingCycle?: BillingCyclePreviewProto;
    recurringBillingCycle?: BillingCyclePreviewProto;
  }> {
    const usePreviewMode =
      isOrdersPreviewPurchaseCheckout() &&
      !isOrdersPurchaseCheckout() &&
      !isProductSwitchCheckout();
    const state = getStore().getState();
    // Get the sale item directly from state to avoid using stale state from the hook
    const { saleItem, customerDetails, operation } = state.checkoutOrders;
    const { trialSaleItemId, pspPlanId, legacyNoomPlanId } = saleItem;

    const order = await createOrderRequest(
      operation,
      usePreviewMode ? undefined : accessCode,
      usePreviewMode,
      customerDetails
    );
    const { initialBillingCycle, recurringBillingCycle } =
      extractBillingCycles(order);
    const refundAmount = getRefundAmount(order);
    dispatch(
      setCheckoutOrders({
        order,
        initialBillingCycle,
        recurringBillingCycle,
        refundAmount,
      })
    );
    // All the med checkout UI was migrated to use the new checkout orders slice
    // but we still need to convert the plan to the recommended plan for the pixel tracking,
    // purchase logic and purchase tracking that have not been migrated yet.
    // TODO: MGEXP-3025
    const recommendedPlan = convertCyclesToPlan({
      initialCycle: initialBillingCycle,
      recurringCycle: recurringBillingCycle,
      pspPlanId,
      legacyNoomPlanId,
      trialSaleItemId,
    });
    dispatch(setRecommendedPlan(recommendedPlan));
    remapURLNoomPlanId(legacyNoomPlanId);
    const recurring = recurringBillingCycle?.invoice?.lineItems?.[0];
    const discountAmount = convertMoneyProtoToNumber(
      recurring?.discountInfo?.promoAmount
    );
    dispatch(updatePromoDiscountAmount(discountAmount));
    if (discountAmount) {
      dispatch(updatePromoOrderStatus("applied"));
    }

    // For Apple Purchase Linkout, trial fee is always 0
    if (isApplePurchaseLinkoutEnabled()) {
      dispatch(setTrialFee(0));
    }

    return { initialBillingCycle, recurringBillingCycle };
  }

  async function getOrder(orderUuid: string) {
    const newOrder = await getOrderRequest({ orderUuid });
    dispatch(setCheckoutOrderOperation(newOrder.operations?.[0]));

    const { initialBillingCycle, recurringBillingCycle } =
      extractBillingCycles(newOrder);

    const trialSaleItemId =
      initialBillingCycle?.invoice?.lineItems?.[0]?.saleItemId;

    const newRecommendedPlan = convertCyclesToPlan({
      initialCycle: initialBillingCycle,
      recurringCycle: recurringBillingCycle,
      pspPlanId: null,
      legacyNoomPlanId: null,
      trialSaleItemId,
    });

    dispatch(setRecommendedPlan(newRecommendedPlan));

    dispatch(
      setCheckoutSaleItem({
        saleItemId: recurringBillingCycle?.invoice?.lineItems?.[0]?.saleItemId,
        trialSaleItemId:
          initialBillingCycle?.invoice?.lineItems?.[0]?.saleItemId,
      })
    );

    dispatch(
      setCheckoutOrders({
        order: newOrder,
        initialBillingCycle,
        recurringBillingCycle,
      })
    );

    // For Apple Purchase Linkout, trial fee is always 0
    if (isApplePurchaseLinkoutEnabled()) {
      dispatch(setTrialFee(0));
    }
  }

  async function initializeCheckout() {
    const { saleItem } = getStore().getState().checkoutOrders;
    const task = trackTask("InitializeOrdersCheckout", {
      accessCode,
      saleItemId: saleItem?.saleItemId,
      trialSaleItemId: saleItem?.trialSaleItemId,
    });
    try {
      task.start();
      dispatch(setCheckoutCustomerDetails(undefined));
      await createOrderAndBillingCyclePreview();
      task.success();
    } catch (ex) {
      task.failed(ex);
      dispatch(setHasInitializationFailed(true));
      captureException(ex);
      dispatch(
        updateCheckoutState({
          purchaseErrorCode: errorConstants.errorCreateOrder,
        })
      );
    }
  }

  /**
   * Executes an order directly via billing-core.
   */
  async function executeOrder() {
    const { order } = getStore().getState().checkoutOrders;
    if (!order?.uuid) {
      throw new Error("No order to execute");
    }

    try {
      dispatch(
        updateCheckoutState({
          processing: {
            isProcessing: true,
            message: undefined,
          },
        })
      );

      const executedOrder = await executeOrderRequest(order.uuid);
      return executedOrder;
    } catch (ex) {
      captureException(ex);
      dispatch(
        updateCheckoutState({
          purchaseErrorCode: errorConstants.errorCreateOrder,
        })
      );
      throw ex;
    } finally {
      dispatch(
        updateCheckoutState({
          processing: {
            isProcessing: false,
            message: undefined,
          },
        })
      );
    }
  }

  return {
    initializeCheckout: useCallbackRef(initializeCheckout),
    createOrderAndBillingCyclePreview: useCallbackRef(
      createOrderAndBillingCyclePreview
    ),
    getOrder: useCallbackRef(getOrder),
    setPurchaseOperation: useCallbackRef(setPurchaseOperation),
    executeOrder: useCallbackRef(executeOrder),
  };
}
